(() => {
  if (window.__CleenlabFreeGiftSelectorInitialized) return;
  window.__CleenlabFreeGiftSelectorInitialized = true;

  const FREE_GIFT_PROPERTY_KEY = '_Free Gift';
  const FREE_GIFT_PROPERTY_ALT_KEY = '_Free_Gift';
  const FREE_GIFT_LABEL_KEY = '_Free Gift Label';
  const SKU_SUFFIX = '-free';
  let isEnforcingThresholds = false;
  let isProcessingChange = false; // guard: blocks MutationObserver during change handler
  let lastChangeFinishedAt = 0;   // cooldown timestamp after change handler completes
  const CHANGE_COOLDOWN_MS = 600; // ignore MutationObserver updates for this long after a change

  /* ── Store which random option variant IDs are shown, keyed by scope ── */
  const shownVariantIdsMap = {}; /* { scope: [variantId, …] } */
  /* ── Store last tier per scope (survives DOM replacement) ── */
  const lastTierMap = {};         /* { scope: number } */

  function getSelectors() {
    return Array.from(document.querySelectorAll('[data-free-gift-selector]'));
  }

  function getThresholds(selector) {
    const one = Number(selector?.dataset.freeGiftThresholdOne || 7000);
    const two = Number(selector?.dataset.freeGiftThresholdTwo || 12000);
    return { one, two };
  }

  function isFreeGiftLineItem(item) {
    if (!item) return false;
    if (item.properties) {
      const value =
        item.properties[FREE_GIFT_PROPERTY_KEY] ||
        item.properties[FREE_GIFT_PROPERTY_ALT_KEY] ||
        item.properties['_free_gift'];
      if (String(value || '').toLowerCase() === 'true') return true;
    }
    if (item.sku && String(item.sku).toLowerCase().endsWith(SKU_SUFFIX)) return true;
    return false;
  }

  function getFreeGiftLineItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter((item) => isFreeGiftLineItem(item));
  }

  function getQualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      if (isFreeGiftLineItem(item)) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function getMaxGiftsFromCart(cart, selector) {
    const qualifyingTotal = getQualifyingTotal(cart);
    const { one, two } = getThresholds(selector || getSelectors()[0]);
    if (qualifyingTotal >= two) return 2;
    if (qualifyingTotal >= one) return 1;
    return 0;
  }

  function getSelectedGiftVariantIds(cart) {
    const selectedIds = [];
    getFreeGiftLineItems(cart).forEach((item) => {
      const variantId = Number(item.variant_id);
      if (variantId && !selectedIds.includes(variantId)) {
        selectedIds.push(variantId);
      }
    });
    return selectedIds;
  }

  function getLabelNumber(item) {
    const rawLabel = String(item?.properties?.[FREE_GIFT_LABEL_KEY] || '');
    const parsed = Number((rawLabel.match(/(\d+)/) || [])[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function sortFreeGiftsForRemoval(a, b) {
    const labelDiff = getLabelNumber(b) - getLabelNumber(a);
    if (labelDiff !== 0) return labelDiff;
    const lineDiff = Number(b.line || 0) - Number(a.line || 0);
    if (lineDiff !== 0) return lineDiff;
    return Number(b.id || 0) - Number(a.id || 0);
  }

  function shuffleArray(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function getChoiceCount(maxGifts) {
    if (maxGifts >= 2) return 3;
    if (maxGifts === 1) return 2;
    return 0;
  }

  function getOptionVariantId(el) {
    return el.querySelector('[data-free-gift-checkbox]')?.dataset.variantId || '';
  }

  /* ── Pick random subset of options and show/hide accordingly ── */
  function applyRandomSubset(selector, maxGifts, selectedGiftIds) {
    const scope = selector.dataset.freeGiftScope || 'cart';
    const allOptions = Array.from(selector.querySelectorAll('[data-free-gift-option]'));

    const freeSkuOptions = [];
    allOptions.forEach((el) => {
      const sku = (el.dataset.sku || '').toLowerCase();
      if (sku.endsWith(SKU_SUFFIX)) {
        freeSkuOptions.push(el);
      } else {
        el.style.display = 'none';
      }
    });

    if (maxGifts <= 0 || freeSkuOptions.length === 0) {
      freeSkuOptions.forEach((el) => { el.style.display = 'none'; });
      shownVariantIdsMap[scope] = [];
      lastTierMap[scope] = 0;
      return;
    }

    const choiceCount = getChoiceCount(maxGifts);
    const prevTier = lastTierMap[scope] || 0;
    const currentTier = maxGifts;
    const prevShownIds = shownVariantIdsMap[scope] || [];

    const vidToEl = {};
    freeSkuOptions.forEach((el) => { vidToEl[getOptionVariantId(el)] = el; });
    const allVids = Object.keys(vidToEl);

    const selectedVidSet = new Set(selectedGiftIds.map(String));
    const selectedVids = allVids.filter((vid) => selectedVidSet.has(vid));
    const unselectedVids = allVids.filter((vid) => !selectedVidSet.has(vid));

    let toShowVids;

    if (prevTier !== currentTier || prevShownIds.length === 0) {
      // Tier changed or first load → fresh random pick
      const needed = Math.max(0, choiceCount - selectedVids.length);
      const randomPick = shuffleArray(unselectedVids).slice(0, needed);
      toShowVids = [...selectedVids, ...randomPick];
    } else {
      // Same tier → keep previously shown variant IDs
      let kept = prevShownIds.filter((vid) => allVids.includes(vid));

      selectedVids.forEach((vid) => {
        if (!kept.includes(vid)) kept.push(vid);
      });

      if (kept.length > choiceCount) {
        const keptSelected = kept.filter((vid) => selectedVidSet.has(vid));
        const keptOthers = kept.filter((vid) => !selectedVidSet.has(vid));
        kept = [...keptSelected, ...keptOthers.slice(0, choiceCount - keptSelected.length)];
      } else if (kept.length < choiceCount) {
        const remaining = allVids.filter((vid) => !kept.includes(vid));
        kept = [...kept, ...shuffleArray(remaining).slice(0, choiceCount - kept.length)];
      }

      toShowVids = kept;
    }

    // Final cap
    if (toShowVids.length > choiceCount) {
      const sel = toShowVids.filter((vid) => selectedVidSet.has(vid));
      const rest = toShowVids.filter((vid) => !selectedVidSet.has(vid));
      toShowVids = [...sel, ...rest.slice(0, Math.max(0, choiceCount - sel.length))];
    }

    // Apply visibility
    const showSet = new Set(toShowVids);
    freeSkuOptions.forEach((el) => {
      el.style.display = showSet.has(getOptionVariantId(el)) ? 'flex' : 'none';
    });

    shownVariantIdsMap[scope] = toShowVids;
    lastTierMap[scope] = currentTier;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not load cart data.');
    return response.json();
  }

  async function addGiftToCart(variantId, labelNumber) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          [FREE_GIFT_PROPERTY_KEY]: 'true',
          [FREE_GIFT_LABEL_KEY]: `Free Gift ${labelNumber}`,
          '_Discount Tag': 'Free Gift',
        },
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.description || 'Could not add the free gift to your cart.');
    }
  }

  async function changeLineItemQuantity(lineItemKey, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity }),
    });
    if (!response.ok) throw new Error('Could not update your cart.');
  }

  async function removeLineItemByKey(lineItemKey) {
    await changeLineItemQuantity(lineItemKey, 0);
  }

  async function removeGiftFromCart(variantId, cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const keysToRemove = cart.items
      .filter((item) => isFreeGiftLineItem(item) && Number(item.variant_id) === Number(variantId))
      .map((item) => item.key)
      .filter(Boolean);
    for (const key of keysToRemove) {
      await removeLineItemByKey(key);
    }
  }

  async function enforceFreeGiftQuantities(cartInput) {
    if (!cartInput || !Array.isArray(cartInput.items)) {
      return { cart: cartInput, changed: false };
    }
    let workingCart = cartInput;
    let changed = false;
    let guard = 0;
    while (guard < 10) {
      const invalidGiftLine = getFreeGiftLineItems(workingCart).find(
        (item) => Number(item.quantity || 0) !== 1 && item?.key
      );
      if (!invalidGiftLine) break;
      await changeLineItemQuantity(invalidGiftLine.key, 1);
      workingCart = await fetchCart();
      changed = true;
      guard += 1;
    }
    return { cart: workingCart, changed };
  }

  async function enforceThresholdRules(cartInput, selector) {
    if (isEnforcingThresholds) return { cart: cartInput, changed: false };
    if (!cartInput || !Array.isArray(cartInput.items)) return { cart: cartInput, changed: false };
    isEnforcingThresholds = true;
    try {
      let workingCart = cartInput;
      let changed = false;
      let guard = 0;
      while (guard < 5) {
        const maxGifts = getMaxGiftsFromCart(workingCart, selector);
        const freeGiftItems = getFreeGiftLineItems(workingCart).sort(sortFreeGiftsForRemoval);
        if (freeGiftItems.length <= maxGifts) break;
        const itemToRemove = freeGiftItems[0];
        if (!itemToRemove?.key) break;
        await removeLineItemByKey(itemToRemove.key);
        workingCart = await fetchCart();
        changed = true;
        guard += 1;
      }
      return { cart: workingCart, changed };
    } finally {
      isEnforcingThresholds = false;
    }
  }

  function setSelectorMessage(selector, text, isError = false) {
    const messageEl = selector.querySelector('[data-free-gift-message]');
    if (!messageEl) return;
    messageEl.innerHTML = text;
    messageEl.classList.toggle('is-error', Boolean(isError));
  }

  /* ── Update a single selector's UI based on cart state ── */
  function updateSelectorState(selector, cart) {
    const maxGifts = getMaxGiftsFromCart(cart, selector);
    const selectedGiftIds = getSelectedGiftVariantIds(cart);
    const selectedCount = selectedGiftIds.length;
    const statusEl = selector.querySelector('[data-free-gift-status]');
    const titleEl = selector.querySelector('[data-free-gift-selector-title], .cl-free-gift-selector__title');
    const subtitleEl = selector.querySelector('[data-free-gift-subtitle]');
    const checkboxes = Array.from(selector.querySelectorAll('[data-free-gift-checkbox]'));

    // Always clear loading state — prevents stuck pointer-events:none
    selector.classList.remove('is-loading');

    selector.classList.toggle('is-hidden', maxGifts <= 0);
    selector.dataset.freeGiftMax = String(maxGifts);
    selector.dataset.freeGiftSelectedCount = String(selectedCount);

    if (statusEl) {
      statusEl.textContent = `${Math.min(selectedCount, maxGifts)}/${maxGifts} selected`;
    }
    if (titleEl) {
      titleEl.textContent = maxGifts >= 2 ? '\uD83C\uDF81 Choose your free gifts!' : '\uD83C\uDF81 Choose your free gift!';
    }
    if (subtitleEl) {
      if (maxGifts >= 2) {
        subtitleEl.textContent = 'You can choose 2 different gifts.';
      } else if (maxGifts === 1) {
        subtitleEl.textContent = 'You can choose 1 free gift.';
      } else {
        subtitleEl.textContent = 'Add at least \u20AC70 (excluding free gifts) to unlock your free gift.';
      }
    }

    // Apply random subset visibility
    applyRandomSubset(selector, maxGifts, selectedGiftIds);

    // Update checkbox states AFTER visibility is set
    checkboxes.forEach((checkbox) => {
      const variantId = Number(checkbox.dataset.variantId);
      const checkedInCart = selectedGiftIds.includes(variantId);
      const optionEl = checkbox.closest('[data-free-gift-option]');
      const isVisible = optionEl && optionEl.style.display !== 'none';

      checkbox.checked = checkedInCart;

      if (checkedInCart) {
        checkbox.disabled = false;
      } else if (!isVisible) {
        checkbox.disabled = true;
      } else {
        checkbox.disabled = maxGifts <= 0 || selectedCount >= maxGifts;
      }
    });

    if (maxGifts > 0) {
      setSelectorMessage(selector, '');
    }
  }

  function updateAllSelectorStates(cart) {
    getSelectors().forEach((selector) => updateSelectorState(selector, cart));
  }

  async function publishCartUpdate(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift-selector',
        cartData: cart,
      });
    }
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cartData: cart, source: 'free-gift-selector' },
      })
    );
  }

  async function syncSelectorsWithCart(cart, selector) {
    let workingCart = cart;
    let didChangeCart = false;
    const quantityEnforced = await enforceFreeGiftQuantities(workingCart);
    workingCart = quantityEnforced.cart;
    didChangeCart = didChangeCart || quantityEnforced.changed;
    const thresholdEnforced = await enforceThresholdRules(workingCart, selector);
    workingCart = thresholdEnforced.cart;
    didChangeCart = didChangeCart || thresholdEnforced.changed;
    if (didChangeCart) await publishCartUpdate(workingCart);
    updateAllSelectorStates(workingCart);
    return workingCart;
  }

  /* ── Delayed re-sync: catches Shopify section rendering that replaces DOM after our JS ran ── */
  let resyncTimer = null;
  function scheduleResync(cart) {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(async () => {
      try {
        const freshCart = cart || await fetchCart();
        updateAllSelectorStates(freshCart);
      } catch (e) {
        console.error('Free gift resync error:', e);
      }
    }, 350);
  }

  /* ── Click handler — determines add/remove from CART state, not checkbox state ── */
  document.addEventListener('click', async (event) => {
    const checkbox = event.target.closest('[data-free-gift-checkbox]');
    /* Also handle clicks on the label that wraps the checkbox */
    const label = !checkbox ? event.target.closest('[data-free-gift-option]') : null;
    const actualCheckbox = checkbox || (label ? label.querySelector('[data-free-gift-checkbox]') : null);
    if (!actualCheckbox) return;

    /* Prevent default checkbox toggle — we set state from cart truth */
    event.preventDefault();

    const selector = actualCheckbox.closest('[data-free-gift-selector]');
    if (!selector) return;
    const variantId = Number(actualCheckbox.dataset.variantId);
    if (!variantId) return;

    /* Block rapid clicks while a change is being processed */
    if (isProcessingChange || selector.classList.contains('is-loading')) {
      return;
    }

    isProcessingChange = true;
    selector.classList.add('is-loading');

    try {
      let cartBefore = await fetchCart();
      const enforcedBefore = await enforceThresholdRules(cartBefore, selector);
      cartBefore = enforcedBefore.cart;
      if (enforcedBefore.changed) await publishCartUpdate(cartBefore);
      const maxGifts = getMaxGiftsFromCart(cartBefore, selector);
      const selectedBefore = getSelectedGiftVariantIds(cartBefore);

      /* Determine add/remove from CART state (not checkbox — browser may pre-toggle it) */
      const isAlreadyInCart = selectedBefore.includes(variantId);

      if (!isAlreadyInCart) {
        /* ── ADD gift ── */
        if (maxGifts <= 0) {
          setSelectorMessage(selector, 'You are not eligible for a free gift yet.', true);
          return;
        }
        if (selectedBefore.length >= maxGifts) {
          setSelectorMessage(
            selector,
            `You can select a maximum of ${maxGifts} free gift${maxGifts > 1 ? 's' : ''}.`,
            true
          );
          return;
        }
        const labelNumber = selectedBefore.length + 1;
        await addGiftToCart(variantId, labelNumber);
      } else {
        /* ── REMOVE gift ── */
        await removeGiftFromCart(variantId, cartBefore);
      }

      const cartAfter = await fetchCart();
      await syncSelectorsWithCart(cartAfter, selector);
      await publishCartUpdate(cartAfter);

      // Schedule delayed re-sync to catch Shopify section rendering
      scheduleResync(cartAfter);

    } catch (error) {
      console.error('Free gift selector error:', error);
      setSelectorMessage(selector, error.message || 'Something went wrong while updating your free gift.', true);
    } finally {
      // Remove loading from ALL selectors (old ref might be detached after section rendering)
      selector.classList.remove('is-loading');
      getSelectors().forEach((sel) => sel.classList.remove('is-loading'));
      isProcessingChange = false;
      lastChangeFinishedAt = Date.now();
    }
  });

  async function refreshFromSource(cartData, source) {
    if (source === 'free-gift-selector') {
      if (cartData) updateAllSelectorStates(cartData);
      return;
    }
    /* Skip external cart updates during/shortly after our own change handler */
    if (isProcessingChange) return;
    if (Date.now() - lastChangeFinishedAt < CHANGE_COOLDOWN_MS) return;
    try {
      const fallbackSelector = getSelectors()[0] || null;
      const cart = cartData || (await fetchCart());
      await syncSelectorsWithCart(cart, fallbackSelector);
    } catch (error) {
      console.error('Free gift selector sync error:', error);
    }
  }

  /* ── MutationObserver: re-apply state after Shopify section rendering replaces the DOM ── */
  let mutationTimer = null;
  function observeSectionRendering() {
    const observer = new MutationObserver(() => {
      // Skip during change handler — it handles its own updates + schedules resync
      if (isProcessingChange) return;

      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(async () => {
        // Skip if change handler is running or just finished (cooldown)
        if (isProcessingChange) return;
        if (Date.now() - lastChangeFinishedAt < CHANGE_COOLDOWN_MS) return;
        const selectors = getSelectors();
        if (selectors.length === 0) return;
        try {
          const cart = await fetchCart();
          updateAllSelectorStates(cart);
        } catch (e) {
          console.error('Free gift MutationObserver error:', e);
        }
      }, 300);
    });

    const targets = document.querySelectorAll('cart-drawer, #cart, [data-cart], .cart, main');
    targets.forEach((target) => {
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const cart = await fetchCart();
      await syncSelectorsWithCart(cart, getSelectors()[0] || null);
    } catch (error) {
      console.error('Free gift init cart fetch error:', error);
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        refreshFromSource(event?.cartData, event?.source);
      });
    }
    document.addEventListener('cart:updated', (event) => {
      refreshFromSource(event?.detail?.cartData, event?.detail?.source);
    });

    observeSectionRendering();
  });
})();
